import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Plane,
  Hotel,
  Car,
  UtensilsCrossed,
  MoreHorizontal,
  Phone,
  MapPin,
  StickyNote,
  UserPlus,
  Users,
  Pin,
  PinOff,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ReassignDialog from "./ReassignDialog";

interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  city: string | null;
  emirate: string | null;
  country: string | null;
  avatar_url: string | null;
}

interface Conversation {
  id: string;
  user_id: string;
  assigned_agent_id: string | null;
  status: "open" | "pending" | "closed";
  subject: string | null;
  service: string | null;
  service_id: string | null;
  last_message_at: string;
  last_message_preview: string | null;
  unread_count_agent: number;
  created_at: string;
  user_profile?: UserProfile;
  is_pinned_by_agent?: boolean;
  pinned_at?: string | null;
  is_archived?: boolean;
  archived_at?: string | null;
}

interface ConversationItemProps {
  conversation: Conversation;
  isSelected: boolean;
  isAdmin: boolean;
  currentUserId: string | undefined;
  onSelect: (conv: Conversation) => void;
  onTakeOver: (convId: string) => void;
  onOpenNotes: (conv: Conversation) => void;
  onTogglePin: (convId: string, isPinned: boolean) => void;
  onArchive: (convId: string) => void;
  onUnarchive: (convId: string) => void;
}

const getServiceIcon = (service: string | null) => {
  const iconClass = "text-muted-foreground";
  switch (service) {
    case "flights": return <Plane size={14} className={iconClass} />;
    case "hotels": return <Hotel size={14} className={iconClass} />;
    case "car_rental": return <Car size={14} className={iconClass} />;
    case "restaurants": return <UtensilsCrossed size={14} className={iconClass} />;
    default: return <MoreHorizontal size={14} className={iconClass} />;
  }
};

const getServiceLabel = (service: string | null) => {
  switch (service) {
    case "flights": return "Vols";
    case "hotels": return "Hôtels";
    case "car_rental": return "Voiture";
    case "restaurants": return "Restaurants";
    default: return "Autre";
  }
};

const getStatusIndicator = (status: string) => {
  switch (status) {
    case "open": return "bg-emerald-500";
    case "pending": return "bg-amber-500";
    case "closed": return "bg-muted-foreground/50";
    default: return "bg-muted-foreground";
  }
};

export default function ConversationItem({
  conversation,
  isSelected,
  isAdmin,
  currentUserId,
  onSelect,
  onTakeOver,
  onOpenNotes,
  onTogglePin,
  onArchive,
  onUnarchive,
}: ConversationItemProps) {
  const [reassignOpen, setReassignOpen] = useState(false);
  
  const profile = conversation.user_profile;
  const customerName = profile?.first_name || profile?.last_name 
    ? `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim()
    : profile?.email || "Inconnu";
  const initials = profile?.first_name 
    ? `${profile.first_name.charAt(0)}${profile.last_name?.charAt(0) || ""}`
    : profile?.email?.charAt(0).toUpperCase() || "?";
  const isAssignedToMe = conversation.assigned_agent_id === currentUserId;
  const isUnassigned = !conversation.assigned_agent_id;
  const isPinned = conversation.is_pinned_by_agent;
  const isArchived = conversation.is_archived;

  const location = profile?.city || profile?.emirate || profile?.country;

  return (
    <>
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          onClick={() => onSelect(conversation)}
          className={`
            group relative px-4 py-3 cursor-pointer transition-all duration-150
            hover:bg-accent/40
            ${isSelected ? "bg-accent/60" : ""}
            ${isArchived ? "opacity-60" : ""}
            ${isPinned ? "bg-primary/5 border-l-2 border-l-primary" : ""}
          `}
        >
          {/* Pinned indicator */}
          {isPinned && (
            <Pin size={10} className="absolute top-2 right-2 text-primary" />
          )}
          
          {/* Unread indicator */}
          {conversation.unread_count_agent > 0 && (
            <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-primary" />
          )}

          <div className="flex items-start gap-3">
            {/* Avatar with status indicator */}
            <div className="relative shrink-0">
              <Avatar className={`w-11 h-11 ${isAssignedToMe ? "ring-2 ring-primary/30" : ""}`}>
                {profile?.avatar_url && (
                  <AvatarImage src={profile.avatar_url} alt={customerName} />
                )}
                <AvatarFallback className={`text-sm font-medium ${isAssignedToMe ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                  {initials}
                </AvatarFallback>
              </Avatar>
              {/* Status dot */}
              <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background ${getStatusIndicator(conversation.status)}`} />
            </div>

            <div className="flex-1 min-w-0">
              {/* Name and time */}
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className={`
                  font-medium text-sm truncate
                  ${conversation.unread_count_agent > 0 ? "text-foreground" : "text-foreground/90"}
                `}>
                  {customerName}
                </span>
                <span className="text-[11px] text-muted-foreground shrink-0">
                  {formatDistanceToNow(new Date(conversation.last_message_at), {
                    addSuffix: false,
                    locale: fr,
                  })}
                </span>
              </div>

              {/* Service and Location row */}
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-1">
                <span className="flex items-center gap-1">
                  {getServiceIcon(conversation.service)}
                  {getServiceLabel(conversation.service)}
                </span>
                {location && (
                  <>
                    <span className="text-muted-foreground/40">•</span>
                    <span className="flex items-center gap-0.5 truncate">
                      <MapPin size={10} />
                      {location}
                    </span>
                  </>
                )}
              </div>

              {/* Message preview */}
              <p className={`
                text-xs truncate leading-relaxed
                ${conversation.unread_count_agent > 0 ? "text-foreground/80 font-medium" : "text-muted-foreground"}
              `}>
                {conversation.last_message_preview || "Pas de message"}
              </p>

              {/* Tags row */}
              <div className="flex items-center gap-1.5 mt-1.5">
                {isUnassigned && !isArchived && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive font-medium">
                    Non assigné
                  </span>
                )}
                {isAssignedToMe && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                    Vous
                  </span>
                )}
                {conversation.unread_count_agent > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground font-medium">
                    {conversation.unread_count_agent} nouveau{conversation.unread_count_agent > 1 ? "x" : ""}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </ContextMenuTrigger>

      <ContextMenuContent className="w-52">
        <ContextMenuItem 
          onClick={() => onTogglePin(conversation.id, !!isPinned)} 
          className="gap-2"
        >
          {isPinned ? <PinOff size={14} /> : <Pin size={14} />}
          {isPinned ? "Désépingler" : "Épingler"}
        </ContextMenuItem>
        
        <ContextMenuItem onClick={() => onOpenNotes(conversation)} className="gap-2">
          <StickyNote size={14} />
          Voir les notes
        </ContextMenuItem>

        <ContextMenuSeparator />

        {isUnassigned && !isArchived && (
          <ContextMenuItem onClick={() => onTakeOver(conversation.id)} className="gap-2">
            <UserPlus size={14} />
            Prendre en charge
          </ContextMenuItem>
        )}

        {isAdmin && !isArchived && (
          <ContextMenuItem 
            onClick={() => setReassignOpen(true)} 
            className="gap-2"
          >
            <Users size={14} />
            Transférer à...
          </ContextMenuItem>
        )}

        <ContextMenuSeparator />

        {!isArchived ? (
          <ContextMenuItem onClick={() => onArchive(conversation.id)} className="gap-2">
            <Archive size={14} />
            Archiver
          </ContextMenuItem>
        ) : (
          <ContextMenuItem onClick={() => onUnarchive(conversation.id)} className="gap-2">
            <ArchiveRestore size={14} />
            Désarchiver
          </ContextMenuItem>
        )}

        {profile?.phone && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem className="gap-2 text-muted-foreground" disabled>
              <Phone size={14} />
              {profile.phone}
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>

    <ReassignDialog
      open={reassignOpen}
      onOpenChange={setReassignOpen}
      conversationId={conversation.id}
      currentAgentId={conversation.assigned_agent_id}
    />
    </>
  );
}
